import { NewButton, NewButtonVariant } from "@visiblethread/react-components";
import { regenerateOutlineConfirmationModal } from "components/Modal/confModalTypes";
import { modalTypes } from "constants/types";
import { map } from "lodash";
import { bool, func, node, oneOf, string } from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { showErrorModal } from "slices/app/actions/error";
import { showModal } from "slices/app/actions/modals";
import { fetchOutlineCounts } from "slices/comments";
import { selectUploadedDocumentsByEntityType } from "slices/documentUploads/selectors";
import { fetchProposalOutlineByOwner } from "slices/proposalOutline/actions/outlineActions";
import { uploadEntityTypeForOwner } from "slices/proposalOutline/actions/shared";
import {
  selectHasProposalOutline,
  selectIsLockedByAnotherUser,
  selectOutlineId,
  selectOutlineOwner,
} from "slices/proposalOutline/selectors";
import { FileFormat } from "utils/getFileFormat";

const DISABLED_FORMATS = [FileFormat.EXCEL, FileFormat.OTHER];

// Any failure (including the outline-counts resolver not being live yet) omits
// the count rather than show a wrong number.
export const fetchOutlineCommentCount = async (dispatch, outlineId) => {
  if (outlineId == null) {
    return null;
  }
  try {
    const { totalComments } = await dispatch(
      fetchOutlineCounts({ outlineId })
    ).unwrap();
    return totalComments;
  } catch {
    return null;
  }
};

const GenerateOutlineButton = ({
  className,
  content,
  variant = NewButtonVariant.l1,
  onSubmitDocuments,
  disabled,
  leftIcon = null,
}) => {
  const owner = useSelector(selectOutlineOwner);
  const dispatch = useDispatch();
  const entityType = uploadEntityTypeForOwner(owner?.ownerType);
  const documents = useSelector(
    selectUploadedDocumentsByEntityType(entityType)
  );
  const isLockedByOtherUser = useSelector(selectIsLockedByAnotherUser);
  const hasExistingOutline = useSelector(selectHasProposalOutline);
  const outlineId = useSelector(selectOutlineId);

  const onSelectDocuments = (selected = []) => {
    const documentIds = map(selected, "id");

    const validIds = documentIds.filter(
      (id) => typeof id === "number" && id > 0
    );

    if (validIds.length !== documentIds.length) {
      return dispatch(
        showErrorModal(
          "Some documents have invalid IDs. Please refresh the page.",
          "Invalid Document IDs"
        )
      );
    }

    onSubmitDocuments(owner?.ownerId, validIds);
  };

  const onClickButton = async () => {
    if (!owner) {
      return;
    }

    const outlineData = await dispatch(
      fetchProposalOutlineByOwner(owner.ownerType, owner.ownerId, true)
    );

    if (outlineData?.locked && !outlineData?.lockedByCurrentUser) {
      return;
    }

    const openDocumentModal = () =>
      dispatch(
        showModal(modalTypes.SELECT_UPLOADED_DOCUMENTS, {
          documents,
          onSelect: onSelectDocuments,
          disabledFormats: DISABLED_FORMATS,
          disabledTooltip:
            "This document type cannot be used for outline generation",
          disableWordless: true,
        })
      );

    if (hasExistingOutline) {
      const commentCount = await fetchOutlineCommentCount(dispatch, outlineId);
      dispatch(
        showModal(
          modalTypes.CONFIRMATION,
          regenerateOutlineConfirmationModal(
            openDocumentModal,
            dispatch,
            commentCount
          )
        )
      );
    } else {
      openDocumentModal();
    }
  };

  return (
    <NewButton
      className={className}
      data-testid="generate-sections-button"
      disabled={isLockedByOtherUser || disabled}
      leftIcon={leftIcon}
      onClick={onClickButton}
      variant={variant}
    >
      {content}
    </NewButton>
  );
};

GenerateOutlineButton.propTypes = {
  className: string,
  content: string.isRequired,
  disabled: bool,
  leftIcon: node,
  onSubmitDocuments: func.isRequired,
  variant: oneOf(Object.values(NewButtonVariant)),
};

export default GenerateOutlineButton;
